import { useState } from 'react';
import {
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
} from '@fluentui/react-components';
import { Briefcase20Regular, Globe20Regular } from '@fluentui/react-icons';
import { BrandIcon } from '@/components/BrandIcon';
import { useGroupInfo } from '@/hooks/useGroupInfo';
import { useAccProject } from '@/hooks/useAccProject';
import { groupInfo } from '@/services/api/endpoints';
import type { Container } from '@/types/domain';

const PRISM_FRONT_URL = 'https://prism.agence-chabanne.fr';

export const prismProjectUrl = (prismId: string): string =>
  `${PRISM_FRONT_URL}/?tab=Projet&projet=${encodeURIComponent(prismId)}&subtab=Accueil`;

/**
 * Sous-menu « Espaces collaboratifs » d'un projet ou d'un espace de service :
 * les ressources qui vivent HORS de l'add-in (fiche PRISM, bibliothèque
 * SharePoint, équipe Teams, espace ACC).
 *
 * POURQUOI UNE CASCADE. Ces entrées étaient mêlées aux actions de rangement dans
 * un menu à plat où rien ne distinguait « créer un dossier ici » de « quitter
 * l'add-in pour un autre outil ». Regroupées, elles deviennent une destination
 * unique et le menu principal reste court.
 *
 * LE CONTENU EST VÉRIFIÉ, PAS DEVINÉ. Les résolutions (Graph pour SharePoint et
 * Teams, l'API Autodesk pour ACC) ne sont déclenchées qu'à l'ouverture du
 * sous-menu, et une entrée n'est cliquable que si sa cible existe RÉELLEMENT : la
 * majorité des groupes Microsoft 365 n'ont pas d'équipe Teams, et un projet sur
 * cinq n'a pas d'espace ACC. Un lien fabriqué mènerait droit à une erreur.
 *
 * DEUX REQUÊTES ET NON UNE, alors qu'elles partent ensemble : les clés diffèrent
 * (groupe Microsoft 365 d'un côté, numéro d'affaire de l'autre, et un projet peut
 * avoir l'un sans l'autre) et surtout les domaines de panne restent séparés — une
 * indisponibilité d'Autodesk ne doit pas retarder ni casser le lien SharePoint,
 * qui est l'usage principal de ce sous-menu.
 */
export function CollaborationMenu({ container }: { container: Container }) {
  const [open, setOpen] = useState(false);
  const info = useGroupInfo(container.m365GroupId ?? null, open);
  // Seuls les PROJETS ont un numéro d'affaire ; un espace de service n'a rien à
  // rapprocher, et lui poser la question afficherait « aucun espace ACC » sur
  // chacun. Pour un projet, c'est le serveur qui tranche si son `number` est
  // exploitable — la règle du numéro à cinq chiffres n'a qu'un seul propriétaire.
  const isProject = container.kind === 'project';
  const acc = useAccProject(isProject ? container.id : null, open);

  const openPrism = () => {
    if (!container.prismId) return;
    window.open(prismProjectUrl(container.prismId), '_blank', 'noopener');
  };

  const openSharepoint = async () => {
    if (!container.m365GroupId) return;
    // Déjà résolu (le sous-menu a préchargé) : ouverture directe, sans onglet
    // intermédiaire qui clignote.
    const known = info.data?.sharepointUrl;
    if (known) {
      window.open(known, '_blank', 'noopener');
      return;
    }
    // Sinon on ouvre un onglet vide SYNCHRONEMENT : un window.open après await
    // serait avalé par les bloqueurs de fenêtres surgissantes.
    const w = window.open('about:blank', '_blank', 'noopener');
    try {
      const fresh = await groupInfo(container.m365GroupId);
      if (w) w.location.href = fresh.sharepointUrl;
      else window.open(fresh.sharepointUrl, '_blank', 'noopener');
    } catch {
      if (w) w.close();
    }
  };

  const openTeams = () => {
    const url = info.data?.teamsUrl;
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  };

  const openAcc = () => {
    const url = acc.data?.url;
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  };

  const teamsStatus = info.data?.teamsStatus;
  // `acc.isError` compte comme « pas pu vérifier » : le serveur répond 200 pour
  // TOUS les cas de rapprochement (y compris l'échec Autodesk, en `unavailable`),
  // donc un rejet ici est un problème de transport ou d'autorisation.
  const accStatus = acc.isError ? 'unavailable' : acc.data?.status;

  return (
    <Menu open={open} onOpenChange={(_e, d) => setOpen(d.open)}>
      <MenuTrigger disableButtonEnhancement>
        <MenuItem icon={<Globe20Regular />}>Espaces collaboratifs</MenuItem>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {container.kind === 'project' && container.prismId && (
            <MenuItem icon={<Briefcase20Regular />} onClick={openPrism}>
              Fiche projet PRISM
            </MenuItem>
          )}

          {container.m365GroupId && (
            <MenuItem icon={<BrandIcon brand="sharepoint" />} onClick={() => void openSharepoint()}>
              Espace documentaire SharePoint
            </MenuItem>
          )}

          {/* Quatre états, tous dits explicitement : on ne laisse jamais croire
              qu'une équipe existe, et « je n'ai pas pu vérifier » ne se déguise
              pas en « il n'y en a pas ». */}
          {info.isPending && container.m365GroupId && (
            <MenuItem disabled icon={<Spinner size="tiny" />}>
              Recherche de l'équipe Teams…
            </MenuItem>
          )}
          {teamsStatus === 'ok' && (
            <MenuItem icon={<BrandIcon brand="teams" />} onClick={openTeams}>
              Équipe Teams
            </MenuItem>
          )}
          {teamsStatus === 'none' && (
            <MenuItem
              disabled
              icon={<BrandIcon brand="teams" />}
              title="Ce groupe Microsoft 365 n'a pas d'équipe Teams."
            >
              Aucune équipe Teams
            </MenuItem>
          )}
          {(teamsStatus === 'unavailable' || (info.isError && container.m365GroupId)) && (
            <MenuItem
              disabled
              icon={<BrandIcon brand="teams" />}
              title="Impossible de vérifier l'équipe Teams pour le moment."
            >
              Équipe Teams indisponible
            </MenuItem>
          )}

          {/* ESPACE ACC (Autodesk Construction Cloud), projets seulement.
              Six états, tous dits : le rapprochement se fait par le numéro
              d'affaire, il peut donc aboutir, viser un espace archivé, ne rien
              trouver, ne pas être tentable, être ambigu, ou avoir échoué. */}
          {isProject && acc.isPending && (
            <MenuItem disabled icon={<Spinner size="tiny" />}>
              Recherche de l'espace ACC…
            </MenuItem>
          )}
          {accStatus === 'ok' && (
            <MenuItem
              icon={<BrandIcon brand="autodesk" />}
              onClick={openAcc}
              title={acc.data?.projectName ?? undefined}
            >
              Espace ACC
            </MenuItem>
          )}
          {/* Archivé : le lien FONCTIONNE (un projet ACC archivé reste
              consultable), donc on l'offre — mais en le nommant, parce que
              découvrir l'archive en croyant ouvrir le projet vivant ferait
              conclure à un mauvais lien. */}
          {accStatus === 'archived' && (
            <MenuItem
              icon={<BrandIcon brand="autodesk" />}
              onClick={openAcc}
              title={`${acc.data?.projectName ?? ''} — ce projet ACC est archivé.`}
            >
              Espace ACC (archivé)
            </MenuItem>
          )}
          {accStatus === 'ambiguous' && (
            <MenuItem
              disabled
              icon={<BrandIcon brand="autodesk" />}
              title={`Plusieurs projets ACC actifs portent le numéro ${acc.data?.number ?? ''} : impossible de choisir sans risque de vous envoyer sur le mauvais.`}
            >
              Plusieurs espaces ACC
            </MenuItem>
          )}
          {accStatus === 'none' && (
            <MenuItem
              disabled
              icon={<BrandIcon brand="autodesk" />}
              title={`Aucun projet ACC ne porte le numéro ${acc.data?.number ?? ''}.`}
            >
              Aucun espace ACC
            </MenuItem>
          )}
          {/* « Pas de numéro exploitable » n'est pas « pas d'espace » : le projet
              porte un intitulé interne (NC, BIM, MODELE…) là où ACC attend cinq
              chiffres. Même libellé — l'utilisateur n'a rien à faire de la
              nuance — mais une infobulle qui l'explique. */}
          {accStatus === 'no-number' && (
            <MenuItem
              disabled
              icon={<BrandIcon brand="autodesk" />}
              title="Ce projet n'a pas de numéro d'affaire à cinq chiffres : le rapprochement avec ACC n'est pas possible."
            >
              Aucun espace ACC
            </MenuItem>
          )}
          {accStatus === 'unavailable' && (
            <MenuItem
              disabled
              icon={<BrandIcon brand="autodesk" />}
              title="Impossible de joindre Autodesk pour le moment."
            >
              Espace ACC indisponible
            </MenuItem>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
